﻿<script setup lang="ts">
    import saveAs from 'file-saver';
    import { ref, watch, computed } from 'vue';
    import { http } from '@/shared/httpWrapper.js';
    import { getAttachmentMetadata, getAttachment, uploadAttachmentToCode } from '@/shared/helpers/api.ts';
    import { Attachment } from '@/shared/helpers/api/types/entities.ts';
    import { prettyBytes } from '@/shared/helpers/prettyBytes.ts';
    import { useCodeEditStore } from '@/stores/codeEditStore.ts';
    import { BTooltip } from 'buefy';
    import { shortDate } from '@/formatters.ts';
    const codeStore = useCodeEditStore();

    const attachment = ref<Attachment | null>(null);

    const attachmentKey = computed<string | undefined>(() => {
        return codeStore?.selectedCode('CableCode')?.values?.attachmentKey ?? undefined;
    });

    const isLoading = computed<boolean>(() => (attachmentKey?.value ?? '') !== (attachment.value?.key ?? ''));

    const hasSelectedCode = computed(() => {
        return codeStore.selectedCode('CableCode') !== undefined;
    });

    const attachmentSize = computed(() => {
        if (attachment?.value?.blobSizeBytes) {
            return '(' + prettyBytes(attachment.value.blobSizeBytes) + ')';
        } else {
            return '';
        }
    });

    watch(attachmentKey, async (key) => {
        if (!key) {
            attachment.value = null;
            return;
        }
        attachment.value = await getAttachmentMetadata(http, key);
    });

    const downloadAttachment = async () => {
        if (attachment.value) {
            const blob = await getAttachment(http, attachment.value.key);
            if (blob) {
                saveAs(blob, attachment.value.fileName ?? 'attachment');
            }
        }
    };

    const uploadAttachment = async (file: File) => {
        // Capture the target code up front: selection can change while the upload is in
        // flight, and the returned key belongs to the code we uploaded for.
        const selected = codeStore.selectedCode('CableCode');
        const id = selected?.values?.id;
        if (!selected || !id) {
            return;
        }
        const formData = new FormData();
        formData.append('file', file);
        const response = await uploadAttachmentToCode(http, id, formData);
        selected.values.attachmentKey = response.data.attachmentKey;
    };

</script>
<template>
    <div v-if="hasSelectedCode">
        <h3 class="title is-5 mb-2">
            Attachment
        </h3>
        <div v-if="isLoading">
            <p>Loading attachment...</p>
        </div>
        <div
            v-else-if="attachment"
            class="is-flex is-flex-direction-row is-align-items-center">
            <div
                class="is-flex mr-5">
                <b-tooltip
                    class="tooltip"
                    multilined
                    position="is-bottom">
                    <template #content>
                        <div>
                            <p class="mb-2">
                                Click to download the attachment.
                            </p>

                            <span>Last updated: {{ shortDate(attachment.dateUpdated) }}</span>
                        </div>
                    </template>
                    <span>
                        <a
                            href="#"
                            @click="downloadAttachment">
                            <b-icon
                                icon="download"
                                size="is-small" />
                            {{ attachment.fileName }}
                            <span class="is-size-7 has-text-grey">{{ attachmentSize }}</span>
                        </a>

                    </span>
                </b-tooltip>
            </div>
            <div class="is-flex">
                <b-field
                    class="file is-info">
                    <b-upload
                        v-model="attachment"
                        class="file-label"
                        @update:model-value="uploadAttachment">
                        <span class="file-cta">
                            <b-icon
                                class="file-icon"
                                icon="file-replace"
                                size="is-small" />
                            <span class="file-label">Replace</span>
                        </span>
                    </b-upload>
                </b-field>
            </div>
        </div>
        <div v-else>
            <b-field
                class="file is-info">
                <b-upload
                    v-model="attachment"
                    class="file-label"
                    @update:model-value="uploadAttachment">
                    <span class="file-cta">
                        <b-icon
                            class="file-icon"
                            size="is-small"
                            icon="upload" />
                        <span class="file-label">Upload</span>
                    </span>
                </b-upload>
            </b-field>
        </div>
    </div>
</template>
<style scoped>

</style>
